// Vendor library store — a per-user, local-first database of vendors.
// Used by vendor pickers throughout the app; every vendor field draws from this
// single source. Persists via window.__PCP_LIBRARY__ (PCPDesk: OneDrive-synced
// SQLite) or localStorage (plain browser). NOT the server-side public vendors
// table — this is the user's personal vendor directory.
//
// Record shape:
//   { id, name, contactName, phone, email, website, serviceTypes: [],
//     rate: { amount, unit }, locations: [{ label, address, zip }],
//     notes, createdAt, updatedAt }

const VENDOR_LS_KEY = "pcp.vendorLibrary.v1"

// PCPDesk bridge — same pattern as goalLibrary() in goal_library.js.
export function vendorLibrary() {
  return (typeof window !== "undefined" && window.__PCP_LIBRARY__) ? window.__PCP_LIBRARY__ : null
}

// Generate collision-proof id: "v" + base36 timestamp + random suffix.
function _vendorId() {
  const ts = Date.now().toString(36)
  const rand = Math.random().toString(36).slice(2, 8)
  return `v${ts}${rand}`
}

// Read the full vendor store. Returns { vendors: { id: record } } or empty.
function _readStore() {
  const lib = vendorLibrary()
  let raw = null
  if (lib && typeof lib.vendorGetAll === "function") {
    try { raw = lib.vendorGetAll() } catch (_) { /* ignore */ }
  } else {
    try { raw = localStorage.getItem(VENDOR_LS_KEY) } catch (_) { /* ignore */ }
  }
  if (!raw) return { vendors: {} }
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === "object" && typeof parsed.vendors === "object" && parsed.vendors !== null) {
      return parsed
    }
    return { vendors: {} }
  } catch (_) {
    return { vendors: {} }
  }
}

// Write the full vendor store.
function _writeStore(store) {
  const lib = vendorLibrary()
  const json = JSON.stringify(store)
  if (lib && typeof lib.vendorSetAll === "function") {
    try { lib.vendorSetAll(json) } catch (_) { /* ignore */ }
  } else {
    try { localStorage.setItem(VENDOR_LS_KEY, json) } catch (_) { /* ignore */ }
  }
}

// Normalize a vendor record, filling defaults for missing fields.
function _normalizeVendor(fields, existing = {}) {
  return {
    id:           existing.id || fields.id || _vendorId(),
    name:         (fields.name != null ? fields.name : existing.name) || "",
    contactName:  (fields.contactName != null ? fields.contactName : existing.contactName) || "",
    phone:        (fields.phone != null ? fields.phone : existing.phone) || "",
    email:        (fields.email != null ? fields.email : existing.email) || "",
    website:      (fields.website != null ? fields.website : existing.website) || "",
    serviceTypes: Array.isArray(fields.serviceTypes) ? fields.serviceTypes : (existing.serviceTypes || []),
    rate:         (fields.rate && typeof fields.rate === "object")
                    ? { amount: fields.rate.amount || "", unit: fields.rate.unit || "per_hour" }
                    : (existing.rate || { amount: "", unit: "per_hour" }),
    locations:    Array.isArray(fields.locations) ? fields.locations : (existing.locations || []),
    notes:        (fields.notes != null ? fields.notes : existing.notes) || "",
    createdAt:    existing.createdAt || new Date().toISOString(),
    updatedAt:    new Date().toISOString(),
  }
}

// Return all vendors as an array.
export function vendorAll() {
  const store = _readStore()
  const vendors = []
  for (const id of Object.keys(store.vendors)) {
    const v = store.vendors[id]
    if (v && typeof v === "object") vendors.push(v)
  }
  return vendors
}

// Return all vendors sorted by name (case-insensitive).
export function vendorAllSorted() {
  return vendorAll().sort((a, b) => {
    const nameA = (a.name || "").toLowerCase()
    const nameB = (b.name || "").toLowerCase()
    return nameA.localeCompare(nameB)
  })
}

// Get a single vendor by id. Returns null if not found.
export function vendorGet(id) {
  const store = _readStore()
  const v = store.vendors[id]
  return (v && typeof v === "object") ? v : null
}

// Add a new vendor. Returns the new id.
export function vendorAdd(fields) {
  const store = _readStore()
  const vendor = _normalizeVendor(fields)
  store.vendors[vendor.id] = vendor
  _writeStore(store)
  return vendor.id
}

// Update an existing vendor. Merges fields with existing record.
export function vendorUpdate(id, fields) {
  const store = _readStore()
  const existing = store.vendors[id]
  if (!existing) return
  store.vendors[id] = _normalizeVendor(fields, existing)
  _writeStore(store)
}

// Delete a vendor by id.
export function vendorDelete(id) {
  const store = _readStore()
  delete store.vendors[id]
  _writeStore(store)
}

// Resolve vendor display data for a resource entry. When vendorId is set and
// found in the library, library values win for name/phone/email/website/address.
// When vendorId is not set OR the library record is missing, the entry's own
// legacy fields are used. Returns { name, phone, email, website, address, linked,
// unlinked }. `linked` is true when library data was used; `unlinked` is true
// when a vendorId was present but the library record was not found (orphan link).
// `vendorGetFn` defaults to vendorGet but can be injected for testing.
export function resolveVendorDisplay(entry, vendorGetFn = vendorGet) {
  const e = entry || {}
  const vendorId = e.vendorId || ""
  const libVendor = vendorId ? vendorGetFn(vendorId) : null

  // Linked to library AND record exists — library values win
  if (vendorId && libVendor) {
    const locs = Array.isArray(libVendor.locations) ? libVendor.locations : []
    const address = locs.length > 0 && locs[0].address ? locs[0].address : ""
    return {
      name:     libVendor.name || "",
      phone:    libVendor.phone || "",
      email:    libVendor.email || "",
      website:  libVendor.website || "",
      address:  address,
      linked:   true,
      unlinked: false
    }
  }

  // Linked but library record is missing — fall back to legacy with unlinked flag
  if (vendorId && !libVendor) {
    return {
      name:     e.name || "",
      phone:    e.phone || "",
      email:    e.email || "",
      website:  e.website || "",
      address:  e.address || "",
      linked:   false,
      unlinked: true
    }
  }

  // Not linked — use legacy fields directly
  return {
    name:     e.name || "",
    phone:    e.phone || "",
    email:    e.email || "",
    website:  e.website || "",
    address:  e.address || "",
    linked:   false,
    unlinked: false
  }
}

// Search vendors. Query matches name, contactName, serviceTypes, or notes
// (case-insensitive). Options: { serviceType, zip } for filtering.
export function vendorSearch(query, options = {}) {
  const q = (query || "").toLowerCase().trim()
  const filterServiceType = (options.serviceType || "").toLowerCase().trim()
  const filterZip = (options.zip || "").trim()

  return vendorAll().filter(v => {
    // Apply serviceType filter
    if (filterServiceType) {
      const types = (v.serviceTypes || []).map(t => t.toLowerCase())
      if (!types.includes(filterServiceType)) return false
    }

    // Apply zip filter — match any location's zip
    if (filterZip) {
      const locs = v.locations || []
      const hasZip = locs.some(loc => (loc.zip || "") === filterZip)
      if (!hasZip) return false
    }

    // Apply text query
    if (q) {
      const name = (v.name || "").toLowerCase()
      const contact = (v.contactName || "").toLowerCase()
      const notes = (v.notes || "").toLowerCase()
      const types = (v.serviceTypes || []).join(" ").toLowerCase()
      const searchable = `${name} ${contact} ${types} ${notes}`
      if (!searchable.includes(q)) return false
    }

    return true
  })
}
